import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCustomerNavigate } from 'hooks/useCustomerNavigate';
import { getInlineString, getText } from 'i18n/intl';
import { keyBy, mapValues, partition } from 'lodash';
import {
  Association,
  Attribute,
  CommonBatchExportParameters,
} from 'types/exportObjects.type';
import { TObjectExportJob } from 'types/objectJob.type';
import { TType } from '@timeedit/types';
import { Alert, Icon, Segmented, TEInput } from '@timeedit/ui-components';
import {
  Checkbox,
  Divider,
  Form,
  FormInstance,
  Modal,
  Select,
  Tooltip,
  Typography,
} from '@timeedit/ui-components/antd';
import useExportAttributes from '../../hooks/useExportAttributes';
import { JobObjectSelection, useJobSubmit } from '../../hooks/useJobSubmit';
import useShowAssociations from '../../hooks/useShowAssociations';
import useShownFields from '../../hooks/useShownFields';
import {
  useReuseExportJobMutation,
  useStartExportJobMutation,
} from './api/exportObjectsApi';
import './ObjectManagerExportModal.scss';

type Props = {
  selectedObjectsCount: number;
  open: boolean;
  onClose: () => void;
  objectType: TType | null;
  jobObjectSelection: JobObjectSelection;
  jobToReuse?: TObjectExportJob;
  redirectOnSuccess?: boolean;
};

type FormData = Omit<CommonBatchExportParameters, 'separateSheets'> & {
  associations: Partial<
    Record<Association, { enabled: boolean; type: 'same' | 'separate' }>
  >;
};

const FORMAT_OPTIONS = [
  {
    label: getText('object_manager.export-modal.format.xlsx'),
    value: 'xlsx',
  },
  {
    label: getText('object_manager.export-modal.format.csv'),
    value: 'csv',
  },
];
const DEFAULT_FORMAT = 'xlsx';

const SEPARATOR_OPTIONS = [
  {
    label: getText('object_manager.export-modal.separator.comma'),
    value: ',',
  },
  {
    label: getText('object_manager.export-modal.separator.semicolon'),
    value: ';',
  },
  {
    label: getText('object_manager.export-modal.separator.pipe'),
    value: '|',
  },
  {
    label: getText('object_manager.export-modal.separator.forward-slash'),
    value: '/',
  },
];
const DEFAULT_SEPARATOR = '|';

const ObjectManagerExportModal: React.FC<Props> = ({
  open,
  onClose,
  selectedObjectsCount,
  objectType,
  jobObjectSelection,
  jobToReuse,
  redirectOnSuccess,
}: Props) => {
  const shownFields = useShownFields(objectType);
  const navigate = useCustomerNavigate();
  const {
    showMembers,
    showAvailabilityRelated,
    showOptionalRelated,
    showRelations,
  } = useShowAssociations(objectType);

  const { attributesList } = useExportAttributes({
    showMembers,
    showAvailabilityRelated,
    showOptionalRelated,
    showRelations,
  });

  const shownAssociations = useMemo(() => {
    return attributesList.filter(
      (attr) =>
        [
          'members',
          'availabilityRelations',
          'optionalRelations',
          'relations',
        ].includes(attr.value) && attr.show
    );
  }, [attributesList]);

  const [form] = Form.useForm<FormData>();
  const [startJob] = useStartExportJobMutation();
  const [reuseJob] = useReuseExportJobMutation();
  const [sendingBatch, setSendingBatch] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const getFetchArgs = useCallback(async () => {
    return fromForm(await form.validateFields());
  }, [form]);

  const onSuccess = useCallback(
    (newId: string) => {
      if (!redirectOnSuccess) return;

      navigate(`/objects/jobs/${newId}`);
    },
    [redirectOnSuccess]
  );

  const onExport = useJobSubmit({
    form,
    setSendingBatch,
    setOpen: onClose,
    selection: jobObjectSelection,
    successMessage: redirectOnSuccess
      ? getText('object_manager.export-modal.redirect-message')
      : getText('object_manager.export-modal.success-message'),
    startJob,
    reuseJob,
    getFetchArgs,
    onSuccess,
  });

  const initialValues = useMemo<FormData>(() => {
    if (jobToReuse) {
      return toForm(jobToReuse, shownAssociations);
    }
    return {
      format: DEFAULT_FORMAT,
      separator: DEFAULT_SEPARATOR,
      fields: shownFields.map((field) => field.id),
      attributes: [] as Attribute[],
      associations: keyBy(
        shownAssociations.map((v) => ({
          key: v.value,
          enabled: v.show,
          type: 'same',
        })),
        'key'
      ),
    };
  }, [shownFields, shownAssociations, jobToReuse]);

  if (!objectType) {
    return null;
  }

  const hasSubTypes = objectType.subTypes && objectType.subTypes.length > 0;

  return (
    <Modal
      destroyOnHidden
      title={getInlineString(
        'object_manager.export-modal.title',
        selectedObjectsCount
      )}
      open={open}
      onCancel={onClose}
      okText={getText('object_manager.export-modal.ok-text')}
      okButtonProps={{
        loading: sendingBatch,
      }}
      onOk={onExport}
      width="40vw"
    >
      <Form form={form} initialValues={initialValues}>
        <div className="objects-export-wrapper">
          {hasSubTypes && (
            <Alert
              showIcon
              type="info"
              description={getText(
                'object_manager.export-modal.subtypes-warning'
              )}
            />
          )}
          <General objectType={objectType} />
          <AttributeSelector form={form} />
          <Divider />
          <FieldSelector shownFields={shownFields} form={form} />
          <Divider />
          <AssociationSelector
            form={form}
            shownAssociations={shownAssociations}
          />
        </div>
      </Form>
    </Modal>
  );
};

type GeneralProps = {
  objectType: TType;
};

const General: React.FC<GeneralProps> = ({ objectType }: GeneralProps) => {
  return (
    <>
      <TEInput.LabelWrapper
        label={getText('objects_type')}
        tooltip={getText('object_manager.export-modal.object-type.tooltip')}
      >
        <Typography.Text>{objectType.name}</Typography.Text>
      </TEInput.LabelWrapper>
      <TEInput.LabelWrapper
        label={getText('object_manager.export-modal.id-type.label')}
        tooltip={getText('object_manager.export-modal.id-type.tooltip')}
      >
        <Typography.Text>
          {getText('object_manager.export-modal.id-type.external')}
        </Typography.Text>
      </TEInput.LabelWrapper>
      <TEInput.LabelWrapper
        label={getText('object_manager.export-modal.format.label')}
        tooltip={getText('object_manager.export-modal.format.tooltip')}
      >
        <Form.Item name="format" rules={[{ required: true }]}>
          <Select options={FORMAT_OPTIONS} />
        </Form.Item>
      </TEInput.LabelWrapper>
      <TEInput.LabelWrapper
        label={getText('object_manager.export-modal.separator.label')}
        tooltip={getText('object_manager.export-modal.separator.tooltip')}
      >
        <Form.Item name="separator" rules={[{ required: true }]}>
          <Select options={SEPARATOR_OPTIONS} />
        </Form.Item>
      </TEInput.LabelWrapper>
    </>
  );
};

type FieldSelectorProps = {
  form: FormInstance;
  shownFields: ReturnType<typeof useShownFields>;
};

const FieldSelector: React.FC<FieldSelectorProps> = ({
  shownFields,
  form,
}: FieldSelectorProps) => {
  const selectedFields = Form.useWatch('fields', form);

  const allChecked = selectedFields?.length === shownFields.length;
  const indeterminate =
    selectedFields &&
    shownFields.length > 0 &&
    selectedFields.length > 0 &&
    !allChecked;

  const onChangeAll = useCallback(() => {
    if (allChecked) {
      form.setFieldsValue({
        fields: [],
      });
      return;
    }
    form.setFieldsValue({
      fields: shownFields.map((field) => field.id),
    });
  }, [allChecked, form, shownFields]);

  return (
    <div className="objects-export-fields-wrapper">
      <Typography.Text strong>
        {getText('object_manager.export-modal.fields.label')}
      </Typography.Text>
      <Checkbox
        indeterminate={indeterminate}
        onClick={onChangeAll}
        checked={allChecked}
      >
        {getText('object_manager.export-modal.checkbox-all')}
      </Checkbox>
      <Form.Item name="fields">
        <Checkbox.Group className="objects-export-field-selector-grid-container">
          {shownFields.map((field) => (
            <Checkbox
              key={field.id}
              value={field.id}
              className="objects-export-field-selector-grid-item"
              skipGroup={true}
              checked={selectedFields?.includes(field.id)}
              onChange={(e) => {
                const newSelectedFields = e.target.checked
                  ? [...(selectedFields || []), field.id]
                  : selectedFields?.filter((id) => id !== field.id);
                form.setFieldsValue({
                  fields: newSelectedFields,
                });
              }}
            >
              {field.name}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>
    </div>
  );
};

type AttributeSelectedProps = {
  form: FormInstance;
};

const AttributeSelector: React.FC<AttributeSelectedProps> = ({
  form,
}: AttributeSelectedProps) => {
  const selectedAttributes = Form.useWatch('attributes', form);
  const { attributesList } = useExportAttributes({
    showMembers: false,
    showAvailabilityRelated: false,
    showOptionalRelated: false,
    showRelations: false,
  });

  const selectedAttributesWithoutAssociations = useMemo(
    () =>
      selectedAttributes?.filter(
        (attr: string) =>
          ![
            'members',
            'availabilityRelations',
            'optionalRelations',
            'relations',
          ].includes(attr)
      ),
    [selectedAttributes]
  );

  const selectedAssociations = useMemo(
    () =>
      selectedAttributes?.filter((attr: string) =>
        [
          'members',
          'availabilityRelations',
          'optionalRelations',
          'relations',
        ].includes(attr)
      ),
    [selectedAttributes]
  );

  const shownAttributes = useMemo(() => {
    return attributesList.filter((attr) => attr.show);
  }, [attributesList, selectedAttributesWithoutAssociations]);

  const allChecked =
    selectedAttributesWithoutAssociations?.length === shownAttributes.length;
  const indeterminate =
    selectedAttributesWithoutAssociations &&
    shownAttributes.length > 0 &&
    selectedAttributesWithoutAssociations.length > 0 &&
    !allChecked;

  const onChangeAll = useCallback(() => {
    if (allChecked) {
      form.setFieldsValue({
        attributes: [...(selectedAssociations ?? [])],
      });
      return;
    }
    form.setFieldsValue({
      attributes: [
        ...(selectedAssociations ?? []),
        ...shownAttributes.map((attr) => attr.value),
      ],
    });
  }, [allChecked, form, selectedAssociations, shownAttributes]);

  if (shownAttributes.length === 0) {
    return null;
  }

  return (
    <div className="objects-export-fields-wrapper">
      <Typography.Text strong>
        {getText('object_manager.export-modal.attributes.label')}
      </Typography.Text>
      <Checkbox
        indeterminate={indeterminate}
        onClick={onChangeAll}
        checked={allChecked}
      >
        {getText('object_manager.export-modal.checkbox-all')}
      </Checkbox>
      <Form.Item name="attributes">
        <Checkbox.Group className="objects-export-field-selector-grid-container">
          <Checkbox
            key="extId"
            className="objects-export-field-selector-grid-item"
            checked
            disabled
            skipGroup
          >
            {getText('object_manager.export-modal.object.ext-id')}
          </Checkbox>
          <Checkbox
            key="objectType"
            className="objects-export-field-selector-grid-item"
            checked
            disabled
            skipGroup
          >
            {getText('objects_type')}
          </Checkbox>
          {shownAttributes.map((attr) => (
            <Checkbox
              key={attr.value}
              value={attr.value}
              className="objects-export-field-selector-grid-item"
              skipGroup
              onChange={(e) => {
                const newSelectedAttributes = e.target.checked
                  ? [...(selectedAttributes || []), attr.value]
                  : selectedAttributes?.filter((id) => id !== attr.value);
                form.setFieldsValue({
                  attributes: newSelectedAttributes,
                });
              }}
              checked={selectedAttributesWithoutAssociations?.includes(
                attr.value
              )}
            >
              {attr.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>
    </div>
  );
};

type AssociationSelectorProps = {
  form: FormInstance;
  shownAssociations: ReturnType<typeof useExportAttributes>['attributesList'];
};

const AssociationSelector: React.FC<AssociationSelectorProps> = ({
  form,
  shownAssociations,
}: AssociationSelectorProps) => {
  const associations = Form.useWatch(
    'associations',
    form
  ) as FormData['associations'];
  const format = Form.useWatch('format', form);

  const allChecked =
    associations != null && Object.values(associations).every((a) => a.enabled);
  const someChecked =
    associations != null && Object.values(associations).some((a) => a.enabled);
  const indeterminate = someChecked && !allChecked;

  const onChangeAll = useCallback(() => {
    form.setFieldsValue({
      associations: mapValues(associations ?? {}, (v) => ({
        ...v,
        enabled: !allChecked,
      })),
    });
  }, [allChecked, form, associations, shownAssociations]);

  const separateSheetOptions = useMemo<
    React.ComponentProps<typeof Segmented>['options']
  >(
    () => [
      {
        value: 'same',
        label: getText('object_manager.export-modal.same-sheet'),
        disabled: format !== 'xlsx',
      },
      {
        value: 'separate',
        label: getText('object_manager.export-modal.separate-sheet'),
        disabled: format !== 'xlsx',
      },
    ],
    [format]
  );

  if (!shownAssociations.length) {
    return null;
  }

  return (
    <div className="objects-export-fields-wrapper">
      <Typography.Text strong>
        {getText('object_manager.export-modal.associations.label')}
      </Typography.Text>

      <table className="objects-export-associations-table">
        <thead>
          <tr>
            <th>
              <Checkbox
                indeterminate={indeterminate}
                onClick={onChangeAll}
                checked={allChecked}
              >
                {getText('object_manager.export-modal.checkbox-all')}
              </Checkbox>
            </th>
            <th>
              {getText('object_manager.export-modal.separate-sheet-type')}{' '}
              <Tooltip
                title={getText(
                  'object_manager.export-modal.separate-sheets-info'
                )}
              >
                <span>
                  <Icon alt="info" id="QuestionCircleOutlined" />
                </span>
              </Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          {shownAssociations.map((a) => (
            <tr key={a.value}>
              <td>
                <Form.Item
                  noStyle
                  name={['associations', a.value, 'enabled']}
                  valuePropName="checked"
                >
                  <Checkbox>{a.label}</Checkbox>
                </Form.Item>
              </td>
              <td>
                <Form.Item name={['associations', a.value, 'type']} noStyle>
                  <Segmented size="small" options={separateSheetOptions} />
                </Form.Item>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const toForm = (
  data: CommonBatchExportParameters,
  shownAssociations: { value: string }[]
): FormData => {
  const [associations, attributes] = partition(
    data.attributes,
    (attribute): attribute is Association =>
      [
        'members',
        'relations',
        'optionalRelations',
        'availabilityRelations',
      ].includes(attribute)
  );

  return {
    format: data.format,
    separator: data.separator,
    fields: data.fields,
    attributes: attributes,
    associations: keyBy(
      shownAssociations.map((v) => ({
        key: v.value,
        enabled: associations.includes(v.value as Association),
        type: data.separateSheets.includes(v.value as Association)
          ? 'separate'
          : 'same',
      })),
      'key'
    ),
  };
};

const fromForm = (form: FormData): CommonBatchExportParameters => {
  const { associations, ...values } = form;

  const associationEntries = Object.entries(associations ?? {}).filter(
    (v) => v[1].enabled
  );
  const associationAttributes = associationEntries.map(
    (a) => a[0] as Association
  );
  const separateAssociations =
    values.format === 'xlsx'
      ? associationEntries
          .filter((a) => a[1].type === 'separate')
          .map((a) => a[0] as Association)
      : [];

  return {
    ...values,
    fields: values.fields ?? [],
    attributes: (values.attributes ?? []).concat(associationAttributes),
    separateSheets: separateAssociations,
  };
};

export default ObjectManagerExportModal;
